import { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Checkbox,
  SelectChangeEvent,
  Card,
  CardContent,
  FormControlLabel,
  Box,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useSnackbar } from 'notistack';
import _ from 'lodash';
import axios from 'axios';

// Icons
import PersonIcon from '@mui/icons-material/Person';

// Zustand store
import { useAppStore } from '../../stores';

// Components
import GenericModal from 'components/GenericModal/GenericModal';
import ForgotPassword from 'components/ForgotPassword/ForgotPassword';
import RecordConflictDialog from 'components/RecordConflictDialog/RecordConflictDialog';
import GenericSpinner from 'components/GenericSpinner/GenericSpinner';
import RoleDropdownHOCForUserManagementAccountCreation from 'components/RoleDropdownHOCForUserManagementAccountCreation/RoleDropdownHOCForUserManagementAccountCreation';

// Services
import { callUserManagementUpdate, callAccountManagementUpdate } from './services';

interface formStateType {
  affPinNum: string;
  memberId: string;
}

interface IAccountManagementProps {
  hideTitle?: boolean;
  displayInModal?: boolean;
  selectedUser?: {
    user: {
      userId: number;
      firstName: string;
      lastName: string;
      username: string;
      memberId: string;
    };
    role: {
      roleId: number;
      roleName: string;
    };
    Active: boolean;
  };
  includeRoleDropdown?: boolean;
  closeParentModal?: (value: boolean) => void;
  dropdownValue?: string | number;
  dropDownOnChange?: (e: SelectChangeEvent<string>) => void;
  formState?: formStateType;
  handleFormChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setSelectedPropsValue?: (value: string | number) => void;
  setSelectedPropsText?: (text: string) => void;
  memberIdRequired?: (value: number) => boolean;
  affiliatePinRequired?: (value: number) => boolean;
  reloadUsers?: () => void;
}

const AccountManagement: React.FC<IAccountManagementProps> = (props) => {
  const { enqueueSnackbar } = useSnackbar();

  // Zustand store
  const storeUser = useAppStore((state) => state.user.user);
  const setUser = useAppStore((state) => state.setUser);
  const setUserManagementAccounts = useAppStore((state) => state.setUserManagementAccounts);
  const setSelectedUserToEdit = useAppStore((state) => state.setSelectedUserToEdit);

  // Conditional rendering for modal
  const showTitle = props.hideTitle ? false : true;
  const displayInModal = props.displayInModal ? props.displayInModal : false;
  const isForUserManagement = props.selectedUser && !_.isEmpty(props.selectedUser);

  // Conflict resolution dialog handlers
  const [conflictDialogState, setConflictDialogState] = useState({
    records: [] as object[],
    open: false,
    selectedRecord: null as object | null,
  });

  // State for Active checkbox
  const [isActive, setIsActive] = useState(
    isForUserManagement ? (props.selectedUser as any)?.Active || false : true
  );

  // Update isActive when selectedUser changes
  useEffect(() => {
    if (isForUserManagement && props.selectedUser) {
      setIsActive((props.selectedUser as any).Active || false);
    }
  }, [props.selectedUser, isForUserManagement]);

  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [updatingUser, setUpdatingUser] = useState(false);
  const [emailValidationError, setEmailValidationError] = useState(false);
  const [memberIdValidationError, setMemberIdValidationError] = useState(false);
  const [affiliatePinValidationError, setAffiliatePinValidationError] = useState(false);
  const [currentPayload, setCurrentPayload] = useState<unknown>(null);

  const memberIdVisible = props.memberIdRequired?.(props.dropdownValue as number) ?? false;
  const affiliatePinVisible = props.affiliatePinRequired?.(props.dropdownValue as number) ?? false;

  useEffect(() => {
    if (memberIdVisible) {
      setMemberIdValidationError(!props.formState?.memberId?.trim());
    } else {
      setMemberIdValidationError(false);
    }
  }, [memberIdVisible, props.formState?.memberId]);

  useEffect(() => {
    if (affiliatePinVisible) {
      setAffiliatePinValidationError(!props.formState?.affPinNum?.trim());
    } else {
      setAffiliatePinValidationError(false);
    }
  }, [affiliatePinVisible, props.formState?.affPinNum]);

  // Determine what user to use depending on what is passed in props
  const activeUser = isForUserManagement
    ? props.selectedUser!
    : { user: storeUser as { 
      userId: number; 
      firstName: string; 
      lastName: string; 
      username: string; 
      memberId?: string 
    }, role: { 
      roleId: 1, 
      roleName: '' 
    } 
  };

  useEffect(() => {
    // Resets role modal value in the store
    return () => {
      if (isForUserManagement && props.selectedUser) {
        setSelectedUserToEdit({
          ...props.selectedUser,
          role: { ...props.selectedUser.role, roleId: 1 },
        });
      }
    };
  }, []);

  const handleClose = () => {
    setConflictDialogState({
      ...conflictDialogState,
      open: false,
    });
  };

  const handleListItemClick = (record: unknown) => {
    setConflictDialogState({
      ...conflictDialogState,
      open: false,
      selectedRecord: record as object | null,
    });
    if (currentPayload) {
      submitUpdateAccount(currentPayload as Record<string, unknown>);
    }
  };

  const dropdownOnChange = (e: SelectChangeEvent<string>) => {
    props.setSelectedPropsValue?.(e.target.value);

    // Also call the parent's dropDownOnChange if it exists
    if (props.dropDownOnChange) {
      props.dropDownOnChange(e);
    }

    const roleValue = Number(e.target.value);

    // Show validation errors immediately when fields appear empty
    if (props.memberIdRequired?.(roleValue)) {
      setMemberIdValidationError(!props.formState?.memberId?.trim());
    } else {
      setMemberIdValidationError(false);
    }
    if (props.affiliatePinRequired?.(roleValue)) {
      setAffiliatePinValidationError(!props.formState?.affPinNum?.trim());
    } else {
      setAffiliatePinValidationError(false);
    }

    // For SelectChangeEvent, we use the value directly
    const selectedText = e.target.value || '';
    if (props.selectedUser) {
      props.selectedUser.role.roleName = selectedText;
    }
    props.setSelectedPropsText?.(selectedText);
  };

  const handleFormChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    if (ev.target.name === 'memberId') {
      setMemberIdValidationError(!ev.target.value.trim());
    }
    if (ev.target.name === 'affPinNum') {
      setAffiliatePinValidationError(!ev.target.value.trim());
    }
    props.handleFormChange?.(ev);
  };

  const handleFormSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const firstName = formData.get('firstName') as string;
    const lastName = formData.get('lastName') as string;
    const username = formData.get('username') as string;
    const roleDropdown = formData.get('roleDropdown') as string;

    const currentUserId = activeUser.user.userId;
    let payload: unknown = {};

    if (isForUserManagement) {
      payload = {
        UserToUpdate: {
          ...activeUser.user,
          UserId: currentUserId,
          FirstName: firstName,
          LastName: lastName,
          Username: username,
          IsActive: isActive,
        },
        NewRole: Number(roleDropdown),
        MemberId: props.formState?.memberId,
        AffPinNum: props.formState?.affPinNum,
      };
    } else {
      payload = {
        UserId: currentUserId,
        FirstName: firstName,
        LastName: lastName,
        Username: username,
      };
    }

    setCurrentPayload(payload);
    submitUpdateAccount(payload as Record<string, unknown>);
  };

  const formSubmissionRequest = (request: unknown) => {
    if (request !== null) {
      setUpdatingUser(true);
      // This appears to be incorrectly passing payload as request in original
      // Keeping original behavior
      Promise.resolve(request)
        .then((response: unknown) => {
          const data = (response as { data?: unknown })?.data;
          if (data) {
            if (isForUserManagement) {
              setUserManagementAccounts(Object.values(data as object));
            } else {
              setUser(data as Record<string, unknown>);
            }
            setUpdatingUser(false);
            const { firstName, lastName } = activeUser.user;
            enqueueSnackbar(
              `${isForUserManagement ? firstName + ' ' + lastName + "'s" : 'Your'} account was updated!`,
              { variant: 'success' }
            );
            if (isForUserManagement && props.closeParentModal) {
              props.closeParentModal(false);
            }
          }
        })
        .catch((error: unknown) => {
          if (axios.isCancel(error)) {
            console.warn('Error: ' + (error as { message: string }).message);
          } else {
            const response = (error as { response?: { data?: string } })?.response;
            if (response && response.data) {
              enqueueSnackbar('The following error occurred: ' + response.data, {
                variant: 'error',
              });
            } else {
              enqueueSnackbar('The following error occurred: ' + error, {
                variant: 'error',
              });
            }
          }
          setUpdatingUser(false);
        });
    }
  };

  const submitUpdateAccount = (payload: Record<string, unknown>) => {
    setUpdatingUser(true);
    if (isForUserManagement) {
      callUserManagementUpdate(payload)
        .then(() => {
          setUpdatingUser(false);
          enqueueSnackbar('Success!', { variant: 'success' });
          if (props.closeParentModal) {
            props.closeParentModal(false);
          }
          if (props.reloadUsers) {
            props.reloadUsers();
          }
        })
        .catch(({ response }: { 
          response?: { 
            data?: { 
              error?: { individualRecordConflict?: boolean; message?: string }; 
              recordListing?: unknown[] 
            } 
          } 
        }) => {
          setUpdatingUser(false);
          if (_.get(response, 'data.error.individualRecordConflict')) {
            setConflictDialogState({
              ...conflictDialogState,
              records: (_.get(response, 'data.recordListing') || []) as object[],
              selectedRecord: null,
              open: true,
            });
          } else {
            enqueueSnackbar(
              'Account creation error: ' +
                (_.get(response, 'data.error.message') || 'Unspecified error'),
              { variant: 'error' }
            );
          }
        });
    } else {
      callAccountManagementUpdate(payload)
        .then((response) => {
          setUpdatingUser(false);
          formSubmissionRequest(response);
        })
        .catch(() => {
          setUpdatingUser(false);
          enqueueSnackbar('Account update failed', {
            variant: 'error',
          });
        });
    }
  };

  const afterPasswordResetWasSuccessful = (userRecord: unknown) => {
    if (userRecord) {
      setUser(userRecord as Record<string, unknown>);
      setResetPasswordModalOpen(false);
      enqueueSnackbar('Password update successful!', { variant: 'success' });
    } else {
      enqueueSnackbar('No user record returned!', { variant: 'error' });
    }
  };

  const emailOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailValidationError(
      !e.target.value.match(/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,63})+$/)
    );
  };

  return (
    <Grid container size={{ xs: 12 }}>
      {updatingUser && <GenericSpinner includeOverlay={true} padding={25} />}
      {showTitle && (
        <Grid size={{ xs: 12 }} style={{ marginBottom: '0.5em' }}>
          <h2>Account Management</h2>
        </Grid>
      )}
      <Grid size={{ xs: 12, md: displayInModal ? 12 : 8, lg: displayInModal ? 12 : 6 }}>
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            {showTitle && (
              <Grid container size={{ xs: 12 }} alignItems="center" sx={{ mb: 3 }}>
                <PersonIcon sx={{ color: '#005DA5', mr: 1 }} />
                <span style={{ color: '#005DA5', fontWeight: 500, fontSize: '18px' }}>User Info</span>
              </Grid>
            )}
            <form
              id="update-account-form"
              onSubmit={handleFormSubmit}
              method="post"
              noValidate
            >
              {isForUserManagement && (
                <Grid size={{ xs: 12 }} sx={{ mb: 3 }}>
                  <RoleDropdownHOCForUserManagementAccountCreation
                    selectedUser={props.selectedUser}
                    dropdownOnChange={dropdownOnChange}
                    selectedPropsValue={props.dropdownValue}
                    setSelectedPropsValue={props.setSelectedPropsValue}
                    onChangeInputFields={handleFormChange}
                    memberIdRequired={props.memberIdRequired}
                    affiliatePinRequired={props.affiliatePinRequired}
                  />
                </Grid>
              )}
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    required
                    variant="outlined"
                    label="First Name"
                    defaultValue={activeUser.user.firstName}
                    autoComplete="off"
                    name="firstName"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    required
                    variant="outlined"
                    label="Last Name"
                    defaultValue={activeUser.user.lastName}
                    autoComplete="off"
                    name="lastName"
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    required
                    label="Email / Username"
                    autoComplete="off"
                    defaultValue={activeUser.user.username}
                    onChange={emailOnChange}
                    name="username"
                    helperText={
                      emailValidationError ? 'Please enter a valid email address' : ''
                    }
                    error={emailValidationError}
                  />
                </Grid>
                {props.memberIdRequired?.(props.dropdownValue as number) && (
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      required
                      variant="outlined"
                      label="Member ID"
                      value={props.formState?.memberId || ''}
                      autoComplete="off"
                      name="memberId"
                      onChange={handleFormChange}
                      helperText={
                        memberIdValidationError ? 'Please enter a valid Member ID' : ''
                      }
                      error={memberIdValidationError}
                    />
                  </Grid>
                )}
                {props.affiliatePinRequired?.(props.dropdownValue as number) && (
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      required
                      variant="outlined"
                      label="Affiliate PIN Number"
                      value={props.formState?.affPinNum || ''}
                      autoComplete="off"
                      name="affPinNum"
                      onChange={handleFormChange}
                      helperText={
                        affiliatePinValidationError ? 'Please enter a valid Affiliate Pin Number' : ''
                      }
                      error={affiliatePinValidationError}
                    />
                  </Grid>
                )}
                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Active"
                  />
                </Grid>
              </Grid>

              <Box sx={{ my: 3, width: '100%', borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }} />

              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onClick={() => setResetPasswordModalOpen(true)}
                    sx={{ height: 42 }}
                  >
                    Reset Password
                  </Button>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={
                      emailValidationError ||
                      memberIdValidationError ||
                      affiliatePinValidationError ||
                      (isForUserManagement && !Number(props.dropdownValue))
                    }
                    sx={{ height: 42 }}
                  >
                    {displayInModal ? 'Update User Record' : 'Save Changes'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Grid>
      <RecordConflictDialog
        handleClose={handleClose}
        handleListItemClick={handleListItemClick}
        open={conflictDialogState.open}
        records={conflictDialogState.records}
      />
      <GenericModal
        open={resetPasswordModalOpen}
        onClose={() => setResetPasswordModalOpen(false)}
        contentWidth={25}
        content={
          <Grid container size={{ xs: 12 }} className="p-3">
            <ForgotPassword
              userId={activeUser.user.userId}
              isForAccountManagement={true}
              afterSubmissionSuccessful={afterPasswordResetWasSuccessful}
            />
          </Grid>
        }
      />
    </Grid>
  );
};

export default AccountManagement;
